// Drive calls behind the editor header's Share button, Version history
// panel and location breadcrumb. Identical file in Docs, Sheets and Slides
// (src/features/drive-file/) — change all three copies together.

import { DriveApiError, type DriveItem } from "@/api/driveApi";

const baseUrl = import.meta.env.VITE_DRIVE_BFF_BASE_URL as string;
const enc = encodeURIComponent;

/** Drive's web app — where the links people open point (never the BFF). */
export const DRIVE_WEB_URL = (() => {
    const raw = (import.meta.env as Record<string, string | undefined>).VITE_DRIVE_WEB_URL;
    return (raw && raw.trim().length > 0 ? raw.trim() : "https://drive.fowlguard.com").replace(/\/+$/, "");
})();

export const driveLinks = {
    /** Universal link: Drive finds the item wherever it lives for whoever opens it. */
    open: (id: string) => `${DRIVE_WEB_URL}/open/${enc(id)}`,
    /** A share link's public landing page. */
    shareLink: (token: string) => `${DRIVE_WEB_URL}/shared/${enc(token)}`,
    /** A folder in Drive; null = My Drive. */
    folder: (id: string | null) => (id ? `${DRIVE_WEB_URL}/folder/${enc(id)}` : `${DRIVE_WEB_URL}/`),
    sharedWithMe: () => `${DRIVE_WEB_URL}/shared-with-me`
};

// ── DTOs ─────────────────────────────────────────────────────────────────

export interface FolderRef {
    id: string;
    parentId: string | null;
    name: string;
}

export interface FileVersion {
    id: string;
    versionNumber: number;
    sizeBytes: number;
    contentType: string | null;
    createdBy: string;
    createdAt: string;
}

export type ShareRole = "viewer" | "editor";

export interface PersonAccess {
    id: string;
    email: string;
    role: ShareRole;
    /** Always true for editors; false = view-only, download blocked. */
    allowDownload: boolean;
    grantedBy: string;
    createdAt: string;
    /** "drive" | "connect-chat" | "connect-channel" | "connect-recording" */
    source: string | null;
}

export interface ItemSharing {
    itemId: string;
    ownerEmail: string;
    myRole: "owner" | ShareRole;
    /** Owners and editors can add, change and remove people. */
    canManage: boolean;
    people: PersonAccess[];
}

export type LinkAudience = "anonymous" | "domain";

export interface ShareLink {
    id: string;
    itemId: string;
    token: string;
    permission: "view" | "edit";
    expiresAt: string | null;
    createdAt: string;
    accessCount: number;
    hasPassword: boolean;
    audience: LinkAudience | "email";
    /** Comma-separated recipients when audience = "email". */
    allowedEmails: string | null;
}

// ── Access presets (Drive's role menu) ───────────────────────────────────

/** What the role picker offers — a viewer with download blocked is "view only". */
export type Access = "editor" | "viewer" | "viewer-nodl";

export const ACCESS_ORDER: readonly Access[] = ["editor", "viewer", "viewer-nodl"];

export const ACCESS_LABEL: Record<Access, string> = {
    editor: "Editor",
    viewer: "Viewer",
    "viewer-nodl": "Viewer · no download"
};

export const toAccess = (role: ShareRole, allowDownload: boolean): Access =>
    role === "editor" ? "editor" : allowDownload ? "viewer" : "viewer-nodl";

export const fromAccess = (a: Access): { role: ShareRole; allowDownload: boolean } =>
    a === "editor" ? { role: "editor", allowDownload: true }
        : a === "viewer" ? { role: "viewer", allowDownload: true }
            : { role: "viewer", allowDownload: false };

// ── HTTP ─────────────────────────────────────────────────────────────────

// Bare reason phrases ASP.NET puts in `title` — our own wording is clearer.
const GENERIC_TITLES = /^(not found|forbidden|unauthorized|bad request|internal server error)$/i;

async function call<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body !== undefined) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    let res: Response;
    try {
        res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers, credentials: "omit" });
    } catch {
        throw new DriveApiError("Can't reach Drive — check your connection.", 0);
    }
    if (!res.ok) {
        let message = res.status === 404
            ? "Drive couldn't find this file, or you no longer have access to it."
            : res.status === 403
                ? "You don't have permission to do that."
                : `Drive request failed (${res.status}).`;
        try {
            const body = (await res.json()) as { title?: string; detail?: string };
            const title = body.title?.trim();
            message = body.detail?.trim() || (title && !GENERIC_TITLES.test(title) ? title : "") || message;
        } catch {
            /* not a JSON body — keep our wording */
        }
        throw new DriveApiError(message, res.status);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

export const driveFileApi = {
    /** Root → item (the item itself last). For a shared item it starts at the shared folder. */
    ancestors: (id: string, token: string | undefined) =>
        call<FolderRef[]>(`/api/items/${enc(id)}/ancestors`, token),

    /** Newest first. Owner / editor only. */
    versions: (id: string, token: string | undefined) =>
        call<FileVersion[]>(`/api/files/${enc(id)}/versions`, token),

    /** Adds a new version holding the old bytes; returns the updated file. */
    restoreVersion: (id: string, versionId: string, token: string | undefined) =>
        call<DriveItem>(`/api/files/${enc(id)}/versions/${enc(versionId)}/restore`, token, { method: "POST" }),

    /** A 5-minute anonymous URL the browser can download natively. */
    versionDownloadUrl: async (id: string, versionId: string, token: string | undefined): Promise<string> => {
        const res = await call<{ url: string }>(
            `/api/files/${enc(id)}/versions/${enc(versionId)}/download-link`,
            token,
            { method: "POST" }
        );
        return new URL(res.url, baseUrl).toString();
    },

    sharing: (id: string, token: string | undefined) =>
        call<ItemSharing>(`/api/items/${enc(id)}/permissions`, token),

    addPeople: (
        id: string,
        body: { emails: string[]; role: ShareRole; allowDownload: boolean },
        token: string | undefined
    ) =>
        call<ItemSharing>(`/api/items/${enc(id)}/permissions`, token, {
            method: "POST",
            body: JSON.stringify({ ...body, source: "drive" })
        }),

    updatePerson: (permissionId: string, body: { role?: ShareRole; allowDownload?: boolean }, token: string | undefined) =>
        call<unknown>(`/api/permissions/${enc(permissionId)}`, token, { method: "PATCH", body: JSON.stringify(body) }),

    removePerson: (permissionId: string, token: string | undefined) =>
        call<void>(`/api/permissions/${enc(permissionId)}`, token, { method: "DELETE" }),

    links: (id: string, token: string | undefined) =>
        call<ShareLink[]>(`/api/items/${enc(id)}/shares`, token),

    createLink: (
        id: string,
        body: { permission: "view" | "edit"; audience: LinkAudience; password?: string; expiresAt?: string },
        token: string | undefined
    ) =>
        call<ShareLink>(`/api/items/${enc(id)}/share`, token, { method: "POST", body: JSON.stringify(body) }),

    deleteLink: (shareId: string, token: string | undefined) =>
        call<void>(`/api/shares/${enc(shareId)}`, token, { method: "DELETE" })
};

// ── Helpers ──────────────────────────────────────────────────────────────

/** Loose email check for the "Add people" box — the server re-validates. */
export function looksLikeEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function errorText(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

export function formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 1024) return `${Math.max(0, Math.round(bytes || 0))} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/** "Today 14:05", "Yesterday 09:12", "3 Sep 17:40", "3 Sep 2025 17:40". */
export function formatWhen(iso: string, now: Date = new Date()): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
    const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((startOf(now) - startOf(d)) / 86_400_000);
    if (days === 0) return `Today ${time}`;
    if (days === 1) return `Yesterday ${time}`;
    const date = d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: d.getFullYear() === now.getFullYear() ? undefined : "numeric"
    });
    return `${date} ${time}`;
}

/** Clipboard write with a fallback for browsers that block the async API. */
export async function copyText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        try {
            const area = document.createElement("textarea");
            area.value = text;
            area.setAttribute("readonly", "");
            area.style.position = "fixed";
            area.style.opacity = "0";
            document.body.appendChild(area);
            area.select();
            const ok = document.execCommand("copy");
            area.remove();
            return ok;
        } catch {
            return false;
        }
    }
}
